import type { LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { Icon } from './Icon';
import { useDelayed } from './useDelayed';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'dig' | 'secondary' | 'tertiary';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  icon?: LucideIcon;
  iconEnd?: boolean;
  full?: boolean;
  large?: boolean;
  /** Stack content vertically (label over value, e.g. In / 0:32). */
  stack?: boolean;
  /** Busy: ignores taps; after 300 ms the label becomes `loadingLabel`. */
  loading?: boolean;
  loadingLabel?: string;
  children?: ReactNode;
}

export function Button({
  variant = 'secondary', icon, iconEnd, full, large, stack, loading = false, loadingLabel,
  className, children, onClick, type = 'button', ...rest
}: ButtonProps) {
  const showLoading = useDelayed(loading, 300);
  const classes = [
    styles.button, styles[variant], full && styles.full, large && styles.large, stack && styles.stack,
    !children && styles.icon, className,
  ].filter(Boolean).join(' ');

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (loading) return;
    onClick?.(e);
  };

  const glyph = icon ? <Icon icon={icon} /> : null;
  return (
    <button
      type={type}
      className={classes}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={handleClick}
      {...rest}
    >
      {!iconEnd && glyph}
      {showLoading && loadingLabel ? loadingLabel : children}
      {iconEnd && glyph}
    </button>
  );
}

export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'icon' | 'stack'> {
  icon: LucideIcon;
  /** Required: icon-only buttons need a name (DESIGN_SYSTEM §2.4). */
  'aria-label': string;
}

export function IconButton(props: IconButtonProps) {
  return <Button {...props} />;
}
